"use client"

import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Eye, EyeOff } from 'lucide-react'
import AuthLayout from '@/components/auth/AuthLayout'
import { useVerifyAccountInvite, useActivateAccount } from '@/lib/api/hooks/auth'
import { showToast } from '@/lib/toast'
import { Button } from '@/components/ui/Button'

const AccountSetup = () => {
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  
  // Support both userId and userid (lowercase) for flexibility
  const userId = searchParams.get('userId') || searchParams.get('userid')
  const otp = searchParams.get('otp')
  
  const verifyAccountInviteMutation = useVerifyAccountInvite()
  const activateAccountMutation = useActivateAccount()

  // Redirect if userId or otp is missing
  useEffect(() => {
    if (!userId || !otp) {
      showToast.error('Invalid account setup link')
      navigate('/login', { replace: true })
    }
  }, [userId, otp, navigate])

  const validatePassword = (password: string) => {
    const minLength = 8
    const hasUpperCase = /[A-Z]/.test(password)
    const hasLowerCase = /[a-z]/.test(password)
    const hasNumbers = /\d/.test(password)
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password)

    return password.length >= minLength && hasUpperCase && hasLowerCase && hasNumbers && hasSpecialChar
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!userId || !otp) {
      setError('Invalid account setup link')
      return
    }

    // Validation
    if (!validatePassword(newPassword)) {
      setError('Password must be at least 8 characters and contain uppercase, lowercase, numbers, and special characters')
      return
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setIsLoading(true)

    try {
      // First verify the account invite
      await verifyAccountInviteMutation.mutateAsync({
        userId,
        otp
      })

      // Then activate the account with the password
      await activateAccountMutation.mutateAsync({
        userId,
        password: newPassword
      })

      showToast.success('Account activated successfully')
      navigate('/login')
    } catch (error: any) {
      console.error('Error activating account:', error)
      setError(error?.response?.data?.message || 'Failed to activate account. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  if (!userId || !otp) {
    return null // Will redirect in useEffect
  }

  return (
    <AuthLayout>
      {/* Form Header */}
      <div className="text-center mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 mb-2 font-satoshi">Account Setup</h2>
        <p className="text-gray-600 font-satoshi">Create a new password</p>
      </div>

      {/* New Password Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm font-satoshi">
            {error}
          </div>
        )}

        {/* New Password Field */}
        <div>
          <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2 font-satoshi">
            New Password
          </label>
          <div className="relative">
            <input
              id="newPassword"
              type={showNewPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="2eAhgjsCki1"
              className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forestGreen-100 focus:border-transparent outline-none transition-colors bg-white font-satoshi"
              required
            />
            <button
              type="button"
              onClick={() => setShowNewPassword(!showNewPassword)}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Confirm Password Field */}
        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2 font-satoshi">
            Confirm Password
          </label>
          <div className="relative">
            <input
              id="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="2eAhgjsCki1"
              className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forestGreen-100 focus:border-transparent outline-none transition-colors bg-white font-satoshi"
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Password Requirements */}
        <div className="text-xs text-gray-500 font-satoshi">
          Password must contain at least 8 characters with uppercase, lowercase, numbers, and special characters.
        </div>

        {/* Proceed Button */}
        <Button
          type="submit"
          variant="primary"
          loading={isLoading}
          disabled={!newPassword || !confirmPassword}
          className="w-full font-medium py-3 px-4 rounded-lg font-satoshi"
        >
          {isLoading ? 'Processing...' : 'Proceed'}
        </Button>
      </form>
    </AuthLayout>
  )
}

export default AccountSetup

